import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WidgetRendererComponent } from '../widget-renderer/widget-renderer.component';

@Component({
  selector: 'app-section-renderer',
  standalone: true,
  imports: [CommonModule, WidgetRendererComponent],
  template: `
    <section
      class="relative"
      [class]="sectionClasses"
      [style.background-color]="section.settings?.bgColor || null"
      [style.background-image]="bgImageStyle"
      [style]="section.settings?.customStyle || null"
    >
      <!-- Background overlay for images -->
      @if (section.settings?.bgImage) {
        <div class="absolute inset-0 bg-gradient-to-b from-surface-950/60 via-surface-950/40 to-surface-950/70"></div>
      }

      <!-- Parallax effect images -->
      @if (section.settings?.effect === 'parallax' && section.settings?.effectImages?.length) {
        <div class="absolute inset-0 overflow-hidden">
          @for (img of section.settings.effectImages; track $index) {
            <img
              [src]="img"
              alt=""
              class="absolute inset-0 w-full h-full object-cover"
              [style.opacity]="0.12"
            />
          }
        </div>
      }

      <div class="relative" [class]="containerClass">
        <!-- Section title -->
        @if (section.showTitle && section.title) {
          <div class="mb-12">
            <h2 class="font-heading text-3xl md:text-4xl lg:text-[2.75rem] font-bold leading-tight tracking-[-0.025em]"
                [class]="section.settings?.bgImage ? 'text-white' : 'text-surface-900'"
            >
              {{ section.title }}
            </h2>
            @if (section.subtitle) {
              <p class="mt-4 text-lg max-w-2xl"
                 [class]="section.settings?.bgImage ? 'text-white/70' : 'text-surface-500'">
                {{ section.subtitle }}
              </p>
            }
          </div>
        }

        <!-- Rows -->
        @for (row of section.rows; track $index) {
          <div
            class="grid gap-6 lg:gap-10 mb-8 last:mb-0"
            [style.grid-template-columns]="getGridTemplate(row)"
          >
            @for (col of row.columns; track $index) {
              <div class="min-w-0">
                @if (col.widgetType === 'html' && col.content) {
                  <div class="prose-content" [innerHTML]="col.content"></div>
                } @else if (col.widgetType && col.widgetType !== 'html') {
                  <app-widget-renderer [column]="col" />
                }
              </div>
            }
          </div>
        }
      </div>
    </section>
  `,
})
export class SectionRendererComponent {
  @Input() section: any;

  get sectionClasses(): string {
    const classes = ['py-20 md:py-28'];
    if (this.section.settings?.bgImage) {
      classes.push('bg-cover bg-center bg-no-repeat');
    }
    if (this.section.settings?.customClass) {
      classes.push(this.section.settings.customClass);
    }
    return classes.join(' ');
  }

  get containerClass(): string {
    return this.section.settings?.fullWidth
      ? 'w-full px-6 lg:px-8'
      : 'mx-auto max-w-7xl px-6 lg:px-8';
  }

  get bgImageStyle(): string | null {
    if (!this.section.settings?.bgImage) return null;
    return `url(${this.section.settings.bgImage})`;
  }

  getGridTemplate(row: any): string {
    if (!row.columns?.length) return '1fr';
    return row.columns.map((c: any) => `${c.span || 1}fr`).join(' ');
  }
}
